import { NgIf } from '@angular/common';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { buildCustomModel } from '../../helpers/build-custom-model.helper';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { extractModel } from '../../helpers/extract-model.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { flattenDeep } from '../../helpers/flatten-deep.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { FORM_FACTORIES } from './schaden-angabe.config';
import { SchadenAngabeModel, SchadenObjekt } from './schaden-angabe.model';

interface TableModel extends Record<string, any> {
  parentIndex?: number;
  childIndex?: number;
}

const UNBEKANNT_NACHNAME = 'Unbekannt';

@Component({
  selector:      'gsm-schaden-angabe',
  templateUrl:   './schaden-angabe.component.html',
  styleUrls:     ['./schaden-angabe.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone:    true,
  imports:       [
    NgIf,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    ReactiveFormsModule,
    MatTabGroup,
    MatTab,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMiniFabButton,
    MatCheckbox,
    MatButton,
    MatProgressSpinner,
    DynamicMaterialFormComponent
  ]
})
export class SchadenAngabeComponent extends CustomDynamicFormControlComponent<SchadenAngabeModel> implements OnInit {
  private formService = inject(CustomDynamicFormService);

  rootDataSource: MatTableDataSource<SchadenObjekt> = new MatTableDataSource();
  personDataSource: MatTableDataSource<SchadenObjekt>;
  sacheDataSource: MatTableDataSource<SchadenObjekt>;
  kfzDataSource: MatTableDataSource<SchadenObjekt>;
  tierDataSource: MatTableDataSource<SchadenObjekt>;

  selectedTab = new UntypedFormControl(0);
  selectedItemIndex: number[];
  selectedCategory: string | null;
  isEdit = false;
  showForm = false;

  entryFormModel: any;
  entryFormGroup: UntypedFormGroup;

  isUnknownPerson = false;
  isSieheBeilage = false;
  isLoading = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    this.rootDataSource.data = this.model.value || [];
    this.isSieheBeilage = !!(this.model.additional.paperProcessOverride && !(this.model.value && this.model.value.length));
    this.updateDataSource();
  }

  protected onDisabledChange() {
    if (this.showForm && this.entryFormModel) {
      setModelDisabled(this.entryFormModel, this.model.disabled);
    }
    super.onDisabledChange();
  }

  private _emitChange() {
    this.model.value = this.isSieheBeilage ? [] : this.rootDataSource.data;
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const isValid = !this.showForm && (this.isSieheBeilage || (this.model.value && this.model.value.length));
    return isValid ? null : {schadenAngabe: true};
  }

  updateDataSource() {
    this.personDataSource = new MatTableDataSource(
      this._applyIndexes(this.rootDataSource.data)
        .filter(
          item => !item.geschaedigtesFahrzeugList && !item.geschaedigteSacheList && !item.geschaedigtesTierList
        )
    );
    this.sacheDataSource = new MatTableDataSource(this._extractKey(this.rootDataSource.data, 'geschaedigteSacheList'));
    this.kfzDataSource = new MatTableDataSource(this._extractKey(this.rootDataSource.data, 'geschaedigtesFahrzeugList'));
    this.tierDataSource = new MatTableDataSource(this._extractKey(this.rootDataSource.data, 'geschaedigtesTierList'));
    this.cd.detectChanges();
    this._emitChange();
  }

  setForm(type: string | null, model: TableModel = null, clear: boolean = false) {
    if (!type) {
      this.showForm = false;
      this.isEdit = false;
    } else {
      this._setCategory(type);
      this.showForm = true;

      const args = [];
      if (type === 'kfz' && this.model.additional) {
        args.push(this.model.additional.carBrands, this.model.additional.carTypes);
      }

      // base checkbox state on whether Nachname is "Unbekannt"
      this.isUnknownPerson = !!(model && model.nachname === UNBEKANNT_NACHNAME);

      const appliedModel = this._applyModel(FORM_FACTORIES[type](...args), model, clear);

      if (this.model.disabled) {
        setModelDisabled(appliedModel, true);
      }

      this.entryFormModel = this.formService.fromJSON(appliedModel);
      this.entryFormGroup = this.formService.createFormGroup(this.entryFormModel);

      // emit change, in order for scrolling in parent app to be triggered
      this._emitChange();
    }
  }

  showTab(category: string) {
    const categories = this.model.additional && this.model.additional.categories;
    return !categories || categories.includes(category);
  }

  private _extractKey(collection: any[], key: string) {
    return flattenDeep(
      collection.map((item, parentIndex) => {
        if (item[key] && item[key].length) {
          return this._applyIndexes(item[key], parentIndex);
        }
      })
    )
      .filter(item => !!item);
  }

  private _applyIndexes(collection: any[], parentIndex?: number) {
    return collection.map((el, childIndex) => {
      return {
        parentIndex,
        childIndex,
        ...el
      };
    });
  }

  private _setCategory(type: string) {
    if (type === 'person') {
      this.selectedCategory = '';
    } else if (type === 'sache') {
      this.selectedCategory = 'geschaedigteSacheList';
    } else if (type === 'kfz') {
      this.selectedCategory = 'geschaedigtesFahrzeugList';
    } else if (type === 'tier') {
      this.selectedCategory = 'geschaedigtesTierList';
    }
  }

  private _transformModel(model: any): SchadenObjekt {
    const category = this.selectedCategory;

    switch (category) {
      case 'geschaedigteSacheList':
        model[category] = [
          {
            beschreibung: model.beschreibung
          }
        ];
        delete model.beschreibung;
        break;
      case 'geschaedigtesFahrzeugList':
        model[category] = [
          filterEmptyProps({
            eigentum:                        model.eigentum,
            marke:                           model.marke,
            modell:                          model.modell,
            kennzeichen:                     model.kennzeichen,
            kennzeichenUnbekanntBegruendung: model.kennzeichenUnbekanntBegruendung,
            fileInstance:                    model.fileInstance,
            collisionPickerData:             model.schaden.collisionPickerData
          })
        ];
        delete model.eigentum;
        delete model.marke;
        delete model.modell;
        delete model.kennzeichen;
        delete model.kennzeichenUnbekanntBegruendung;
        delete model.fileInstance;
        delete model.schaden;
        break;
      case 'geschaedigtesTierList':
        model[category] = [
          filterEmptyProps({
            bezeichnung:  model.bezeichnung,
            beschreibung: model.beschreibung
          })
        ];
        delete model.bezeichnung;
        delete model.beschreibung;
        break;
    }

    model = filterEmptyProps(model);
    return model;
  }

  private _applyModel(formModel: any, entityModel?: any, clear?: boolean) {
    formModel.forEach(inputModel => {
      if (inputModel.group) {
        this._applyModel(inputModel.group, entityModel, clear);
      }

      if (!inputModel.value) {
        inputModel.value = '';
      }

      if (entityModel) {
        let flatEntityModel = entityModel;
        if (flatEntityModel.geschaedigteSacheList) {
          flatEntityModel = {
            ...flatEntityModel,
            ...flatEntityModel.geschaedigteSacheList[0]
          };
        }
        if (flatEntityModel.geschaedigtesFahrzeugList) {
          const {
            collisionPickerData,
            kennzeichen
          } = flatEntityModel.geschaedigtesFahrzeugList[0];
          flatEntityModel = {
            ...flatEntityModel,
            ...flatEntityModel.geschaedigtesFahrzeugList[0],
            kennzeichenBekannt: !!kennzeichen,
            schaden:            {collisionPickerData}
          };
        }
        if (flatEntityModel.geschaedigtesTierList) {
          flatEntityModel = {
            ...flatEntityModel,
            ...flatEntityModel.geschaedigtesTierList[0]
          };
        }
        const entityKeys = Object.keys(flatEntityModel);

        if (entityKeys.includes(inputModel.id)) {
          inputModel.value = flatEntityModel[inputModel.id];
        }
      }
    });
    return formModel;
  }

  editItem(type: string, model: TableModel) {
    this._setCategory(type);
    this.selectedItemIndex = [
      model.parentIndex,
      model.childIndex
    ];
    this.isEdit = true;
    if (type !== 'person') {
      this.setForm(type, this.rootDataSource.data[model.parentIndex]);
    } else {
      this.setForm(type, this.rootDataSource.data[model.childIndex]);
    }
    this.updateDataSource();
  }

  removeItem(element: any, category?: string) {
    // new copy of array is needed to trigger change detection
    const source = [...this.rootDataSource.data];
    source.splice(!!category ? element.parentIndex : element.childIndex, 1);
    this.rootDataSource.data = source;
    this.updateDataSource();
  }

  async saveItem(event?: Event) {
    // prevent form submission error on button click
    this.isLoading = true;
    if (event) {
      event.preventDefault();
    }

    // if it's an unknown person, set Nachname to "Unbekannt"
    if (this.selectedCategory === '' && this.isUnknownPerson) {
      this.entryFormGroup.get('person_erfassen.nachname')
        .setValue(UNBEKANNT_NACHNAME);
    }

    const model: any = extractModel(buildCustomModel(this.entryFormModel));

    const source = this.rootDataSource.data;
    const category = this.selectedCategory;

    if (category === 'geschaedigtesFahrzeugList') {
      model.fileInstance = await model.schaden.getScreenshotFile();
    }

    const transformedModel = this._transformModel(model);

    if (this.isEdit) {
      const [parentIndex, childIndex] = this.selectedItemIndex;
      source.splice(!!category ? parentIndex : childIndex, 1, transformedModel);
    } else {
      source.push(transformedModel);
    }

    this.rootDataSource.data = source;
    this.isEdit = false;
    this.selectedCategory = null;
    this.setForm(null);
    this.updateDataSource();
    this.isLoading = false;
  }

  cancelEdit() {
    this.setForm(null);
    this.cd.detectChanges();
    this._emitChange();
  }

  get isEntryFormValid(): boolean {
    if (this.entryFormGroup) {
      const valid = this.entryFormModel && !formElementsInvalid(this.entryFormGroup, this.entryFormModel);
      switch (this.selectedCategory) {
        case '': // person
          return this.isUnknownPerson || valid;
        default:
          return valid;
      }
    }

    return false;
  }

  onUnknownCheckboxChange(isUnknownPerson: boolean) {
    // if checkbox was cleared, make sure "Unbekannt" Nachname is also cleared
    if (!isUnknownPerson) {
      const nachnameControl = this.entryFormGroup && this.entryFormGroup.get('person_erfassen.nachname');
      if (nachnameControl && nachnameControl.value === UNBEKANNT_NACHNAME) {
        nachnameControl.setValue('');
      }
    }
  }

  get isFormDisabled(): boolean {
    return this.model.disabled;
  }

  get isFormHidden(): boolean {
    return this.selectedCategory === '' && this.isUnknownPerson;
  }

  getCategoryObjectCountLabel(dataSource: MatTableDataSource<any>): string {
    return dataSource.data.length ? `\u00A0\u00A0(${dataSource.data.length})` : '';
  }

  onSieheBeilageChange(isSieheBeilage: boolean) {
    if (isSieheBeilage && this.showForm) {
      this.setForm(null);
    }
    this._emitChange();
  }
}
